import {Component} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {AmountPipe} from '@emma-labs/shared/angular';
import {getAnnualPrice, Insurer, SaleReport, snakeToSeparatedPascal} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {InputTextModule} from 'primeng/inputtext';
import {TableModule} from 'primeng/table';
import {debounceTime, of, switchMap} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {SaleReportService} from '../../services/sale/sale-report.service';
import {SHORT_DEBOUNCE_TIME} from '../../utils/utility-constants';
import {MainComponent} from '../main/main.component';
import {TableEntry} from './table-entry';

@UntilDestroy()
@Component({
  selector: 'em-sale-report-search-page',
  templateUrl: './sale-report-search-page.component.html',
  imports: [
    MainComponent,
    CardModule,
    TableModule,
    ButtonModule,
    ReactiveFormsModule,
    InputTextModule,
    AmountPipe,
  ],
})
export class SaleReportSearchPageComponent {
  tableEntries: TableEntry[] = [];
  readonly filter: FormControl<string | undefined>;

  constructor(
    fb: NonNullableFormBuilder,
    private readonly saleReportService: SaleReportService,
    private readonly router: Router,
  ) {
    this.filter = fb.control(undefined);

    this.filter.valueChanges
      .pipe(
        debounceTime(SHORT_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((filter) => {
          if (!filter || filter === '') {
            return of([]);
          }
          return this.saleReportService.search(filter);
        }),
        untilDestroyed(this),
      )
      .subscribe((saleReports) => (this.tableEntries = this.mapSaleReportsToTableEntries(saleReports)));
  }

  openSaleReport(saleReportId: string): void {
    this.router.navigate(['/', 'dashboard', 'sale-report', btoa(saleReportId)]);
  }

  private mapSaleReportsToTableEntries(saleReports: SaleReport[]): TableEntry[] {
    return saleReports.map((saleReport) => {
      const agent = saleReport.proposal.agents.find((agent) => agent.isPrimary);
      if (!agent || !agent.email) {
        throw new Error('Agent not found or email is missing');
      }

      const entry: TableEntry = {
        id: saleReport.id,
        policyNumber: saleReport.policyNumber,
        fullName: `${saleReport.proposal.insuree.firstName} ${saleReport.proposal.insuree.lastName}`,
        insureeEmail: saleReport.proposal.insuree.email,
        advisorEmail: agent.email,
        insurer: saleReport.insurer === Insurer.UNKNOWN ? '--' : snakeToSeparatedPascal(saleReport.insurer),
        annualPremium: getAnnualPrice(saleReport.protection.personInsurances),
      };

      return entry;
    });
  }
}
